import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Wallet, Zap, TrendingUp, Cpu, HeartHandshake, ShieldCheck, Award,
  Star, Quote, MessageCircle, Phone,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import {
  Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext,
} from "../components/ui/carousel";
import Counter from "../components/Counter";
import {
  BRAND, STATS, WHY_CHOOSE, INDUSTRIES, PROCESS_STEPS, TESTIMONIALS, TESTIMONIALS_NOTE, FAQS,
  CUSTOM_SOLUTION_COPY,
} from "../data/site";

const ICONS = { Wallet, Zap, TrendingUp, Cpu, HeartHandshake, ShieldCheck, Award };

export default function Home() {
  const whatsappHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent("Hi Zyvora Digital, I'd like a free consultation.")}`;
  return (
    <div className="bg-[#0A0A0A] text-white">
      {/* HERO */}
      <section data-testid="hero-section" className="relative pt-32 pb-24 lg:pt-44 lg:pb-32 overflow-hidden radial-glow">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="absolute top-20 -right-20 h-[420px] w-[420px] rounded-full bg-[#F5D67B]/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-white/10 mb-7"
            >
              <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
              <span className="text-xs font-medium tracking-wide text-gray-300">Premium Digital Growth Partner</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight"
            >
              Helping Businesses{" "}
              <span className="text-gradient-brand">Grow Online</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-6 text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed"
            >
              We help businesses establish a powerful online presence through websites, branding, local SEO, business profiles, social media, and digital growth solutions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <Link to="/contact" data-testid="hero-cta-primary" className="group">
                <Button
                  className="relative overflow-hidden bg-gradient-brand hover:opacity-95 text-[#0A0A0A] rounded-full h-16 px-9 text-base md:text-lg font-bold w-full sm:w-auto glow-blue shadow-[0_0_60px_-15px_rgba(212,175,55,0.7)] group-hover:-translate-y-1 group-hover:shadow-[0_0_80px_-10px_rgba(245,214,123,0.7)] transition-all duration-300"
                >
                  <span className="relative z-10 flex items-center">
                    Get Free Consultation
                    <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-[#F5D67B] via-[#D4AF37] to-[#F5D67B] bg-[length:200%_100%] opacity-0 group-hover:opacity-100 group-hover:animate-[marquee-left_3s_linear_infinite] transition-opacity" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-cta-secondary" className="group">
                <Button
                  variant="outline"
                  className="border-2 border-white/20 bg-white/5 hover:bg-white/10 hover:border-[#D4AF37]/60 text-white rounded-full h-16 px-9 text-base md:text-lg font-bold w-full sm:w-auto group-hover:-translate-y-1 transition-all duration-300"
                >
                  Explore Services
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-gray-400"
            >
              <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Free 20-min consultation</span>
              <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Response within 24 hours</span>
              <span className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> No obligation quotes</span>
            </motion.div>
          </div>

          {/* HERO VISUAL */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 z-10 relative"
          >
            <div className="relative rounded-3xl overflow-hidden border border-white/10 glass p-6">
              <img
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80"
                alt="Digital growth"
                className="w-full h-80 object-cover rounded-2xl"
              />
              <div className="absolute top-10 -left-6 glass rounded-2xl p-4 w-48 shadow-2xl">
                <p className="text-xs text-gray-400">Enquiries</p>
                <p className="text-2xl font-display font-bold text-white">+248%</p>
                <div className="mt-2 h-1.5 bg-white/10 rounded-full">
                  <div className="h-full w-4/5 bg-gradient-brand rounded-full" />
                </div>
              </div>
              <div className="absolute bottom-10 -right-6 glass rounded-2xl p-4 w-52 shadow-2xl">
                <div className="flex items-center gap-2 mb-1">
                  <div className="h-2 w-2 rounded-full bg-green-400" />
                  <p className="text-xs text-gray-300">Lead from Google</p>
                </div>
                <p className="text-base font-semibold text-white">New enquiry — Salon</p>
                <p className="text-xs text-gray-400 mt-1">2 minutes ago</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="relative py-16 border-y border-white/10 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {STATS.map((s, i) => (
              <div key={i} className="text-center md:text-left" data-testid={`stat-card-${i}`}>
                <div className="font-display text-4xl md:text-5xl font-bold text-gradient-brand">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <p className="text-sm text-gray-400 mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section data-testid="why-choose-section" className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Why Zyvora Digital</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              The growth partner small businesses{" "}
              <span className="text-gradient-brand">actually trust</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((w, i) => {
              const Icon = ICONS[w.icon] || Sparkles;
              return (
                <div
                  key={i}
                  data-testid={`why-card-${i}`}
                  className="group rounded-2xl border border-white/10 bg-[#121212] p-8 hover:border-[#D4AF37]/40 hover:bg-[#1A1A1A] transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="h-12 w-12 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-2">{w.title}</h3>
                  <p className="text-gray-400 leading-relaxed text-sm">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOM SOLUTION BANNER */}
      <section data-testid="custom-solution-banner" className="pb-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl border border-white/10 bg-[#121212] p-8 md:p-12 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-brand opacity-10" />
            <div className="relative grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-2">Custom Solutions</p>
                <h3 className="font-display text-2xl md:text-3xl font-bold leading-snug">
                  {CUSTOM_SOLUTION_COPY}
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:items-stretch">
                <Link to="/contact" data-testid="custom-solution-cta">
                  <Button className="w-full bg-gradient-brand text-[#0A0A0A] rounded-full h-12 px-6 font-semibold">
                    Get Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="custom-solution-whatsapp">
                  <Button variant="outline" className="w-full border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full h-12 px-6 font-semibold">
                    <MessageCircle className="mr-2 h-4 w-4" /> Discuss on WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES marquee */}
      <section data-testid="industries-section" className="py-20 border-y border-white/10 bg-[#121212] overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 mb-10">
          <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3 text-center">Industries We Serve</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-center max-w-2xl mx-auto">
            Trusted by businesses across every category
          </h2>
        </div>
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap gap-4">
            {[...INDUSTRIES, ...INDUSTRIES].map((ind, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-white/10 bg-white/5 text-sm text-gray-200"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" />
                {ind}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section data-testid="process-section" className="py-24 lg:py-32 relative">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-16">
            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Our Client Process</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              A simple, transparent <span className="text-gradient-brand">8-step process</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PROCESS_STEPS.map((p, i) => (
              <div
                key={i}
                data-testid={`process-step-${i}`}
                className="relative rounded-2xl border border-white/10 bg-[#121212] p-7 hover:border-[#D4AF37]/40 transition group overflow-hidden"
              >
                <div className="absolute -right-6 -top-6 font-display text-8xl font-black text-white/[0.03] select-none">
                  {p.step}
                </div>
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-display text-sm font-bold text-[#D4AF37] tracking-wider">STEP {p.step}</span>
                    <div className="h-px flex-1 bg-gradient-to-r from-[#D4AF37]/40 to-transparent" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-2">{p.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="py-24 lg:py-32 bg-[#121212] border-y border-white/10 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">What Clients Say</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight max-w-3xl mx-auto">
              Real stories from real <span className="text-gradient-brand">growing businesses</span>
            </h2>
          </div>

          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent>
              {TESTIMONIALS.map((t, i) => (
                <CarouselItem key={i} className="md:basis-1/2">
                  <div
                    data-testid={`testimonial-card-${i}`}
                    className="h-full rounded-2xl border border-white/10 bg-[#121212] p-8 flex flex-col"
                  >
                    <Quote className="h-8 w-8 text-[#D4AF37] mb-4" />
                    <p className="text-gray-200 leading-relaxed text-base md:text-lg flex-1">"{t.quote}"</p>
                    <div className="flex items-center gap-1 mt-6 text-yellow-400">
                      {[...Array(t.rating)].map((_, idx) => <Star key={idx} className="h-4 w-4 fill-current" />)}
                    </div>
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <p className="font-display font-semibold">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.role}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center gap-3 mt-8">
              <CarouselPrevious data-testid="testimonials-prev" className="static translate-y-0 bg-white/5 border-white/10 text-white hover:bg-white/10" />
              <CarouselNext data-testid="testimonials-next" className="static translate-y-0 bg-white/5 border-white/10 text-white hover:bg-white/10" />
            </div>
          </Carousel>

          <p data-testid="testimonials-note" className="text-center text-gray-400 mt-10 max-w-2xl mx-auto text-sm">
            {TESTIMONIALS_NOTE}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="py-24 lg:py-32 relative">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">FAQs</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              Everything you wanted to <span className="text-gradient-brand">ask</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="rounded-2xl border border-white/10 bg-[#121212] px-5 data-[state=open]:border-[#D4AF37]/40"
              >
                <AccordionTrigger className="text-left font-display font-semibold text-base md:text-lg py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 leading-relaxed text-sm md:text-base pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-final-cta" className="py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-brand opacity-20" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-display text-4xl md:text-6xl font-bold leading-tight mb-6">
            Let's build something <span className="text-gradient-brand">that grows.</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-9">
            Book a free 20-minute consultation. We'll audit your online presence and share a plan to bring you more customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" data-testid="home-final-cta-button">
              <Button className="bg-gradient-brand text-[#0A0A0A] rounded-full h-14 px-8 text-base font-semibold group">
                Get Free Consultation <ArrowRight className="ml-2 h-5 w-5 transition group-hover:translate-x-1" />
              </Button>
            </Link>
            <a href={`tel:${BRAND.phones[0].tel}`} data-testid="home-final-cta-call">
              <Button variant="outline" className="border-white/20 bg-white/5 hover:bg-white/10 text-white rounded-full h-14 px-8 text-base font-semibold">
                <Phone className="mr-2 h-4 w-4" /> {BRAND.phones[0].value}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
